/* Turns the on-screen price sheet into a PDF file (works on mobile too). */
/* Make sure the web fonts (Inter) are ready so the PDF / image use the same font as the screen. */
async function fontsReady() {
  try {
    if (document.fonts && document.fonts.load) {
      await Promise.all(['400 14px Inter', '600 14px Inter', '700 14px Inter'].map((f) => document.fonts.load(f)));
      await document.fonts.ready;
    }
  } catch (e) { /* ignore - fall back to whatever font is loaded */ }
}

export async function sheetToPdfBlob(node) {
  const [h2c, pdf] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const html2canvas = h2c.default || h2c;
  const JsPDF = pdf.jsPDF || pdf.default;
  const host = document.createElement('div');
  host.className = 'pl-cap';
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:1000px;background:#fff;';
  host.appendChild(node.cloneNode(true));
  await fontsReady();
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, { scale: 1.5, backgroundColor: '#ffffff', useCORS: true, windowWidth: 1000 });
    const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const margin = 8;
    const pageW = 210 - margin * 2;
    const pageH = 297 - margin * 2;
    let imgW = pageW;
    let imgH = (canvas.height * imgW) / canvas.width;
    if (imgH > pageH && imgH <= pageH * 1.12) {
      imgH = pageH;
      imgW = (canvas.width * imgH) / canvas.height;
    }
    if (imgH <= pageH + 0.01) {
      doc.addImage(canvas.toDataURL('image/jpeg', 0.82), 'JPEG', margin + (pageW - imgW) / 2, margin, imgW, imgH);
    } else {
      const slicePx = Math.floor((pageH * canvas.width) / pageW);
      let y = 0;
      let first = true;
      while (y < canvas.height) {
        const h = Math.min(slicePx, canvas.height - y);
        const part = document.createElement('canvas');
        part.width = canvas.width;
        part.height = h;
        const ctx = part.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, part.width, part.height);
        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (!first) doc.addPage();
        doc.addImage(part.toDataURL('image/jpeg', 0.82), 'JPEG', margin, margin, pageW, (h * pageW) / canvas.width);
        first = false;
        y += h;
      }
    }
    return doc.output('blob');
  } finally {
    document.body.removeChild(host);
  }
}

export async function buildSheetPdfFile(title) {
  const node = document.querySelector('.pl-print-area .pl-sheet');
  if (!node) return null;
  const blob = await sheetToPdfBlob(node);
  const base = String(title || 'Price List').replace(/[^A-Za-z0-9 _-]+/g, ' ').trim().replace(/\s+/g, '_') || 'Price_List';
  return new File([blob], base + '.pdf', { type: 'application/pdf', lastModified: Date.now() });
}

export async function buildSheetImageFile(title) {
  const node = document.querySelector('.pl-print-area .pl-sheet');
  if (!node) return null;
  const h2c = await import('html2canvas');
  const html2canvas = h2c.default || h2c;
  const host = document.createElement('div');
  host.className = 'pl-cap';
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:1000px;background:#fff;';
  host.appendChild(node.cloneNode(true));
  await fontsReady();
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, { scale: 1.6, backgroundColor: '#ffffff', useCORS: true, windowWidth: 1000 });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    const base = String(title || 'Price List').replace(/[^A-Za-z0-9 _-]+/g, ' ').trim().replace(/\s+/g, '_') || 'Price_List';
    return new File([blob], base + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    document.body.removeChild(host);
  }
}
// PDFSMALL: smaller PDF so WhatsApp (iPhone) uploads it reliably

/* Clipboard on iPhone / Safari only accepts PNG, so convert the JPEG image first. */
export function imageFileToPngBlob(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        c.toBlob((b) => {
          URL.revokeObjectURL(url);
          if (b) resolve(b); else reject(new Error('png failed'));
        }, 'image/png');
      } catch (e) { URL.revokeObjectURL(url); reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}
